import { call, put, takeLatest, fork, delay } from 'redux-saga/effects';
import { isEmpty } from 'lodash';
import {
  requestRegionIds,
  requestGetIdTokenV2,
  logout as logoutApi,
  getAccessLogoutEndpoint,
  requestIdentityProvider,
} from 'apis/login';
import { getRegionIdsRequest, getRegionIdsSuccess } from 'slices/sessionsSlice';
import { setRedirectUrl } from 'slices/redirectSlice';
import sagaActions from 'constants/sagaActions';
import routes from 'constants/routes';
import {
  GetRegionIdsResponse,
  GetIdTokenV2Response,
  GetIdTokenParams,
  GetAccessLogoutEndpointResponse,
  GetIdentityProviderResponse,
  AccessSsoLoginParams,
} from 'types/sessionsTypes';
import {
  redirectToVcIdentity,
  buildCallbackSearch,
  isMfaEnabled,
} from 'utils/sessions';
import { isValidEmail } from 'utils/validate';
import {
  clearSessionFromStorageAndCookies,
} from 'utils/storages';
import {
  isAccessFederatedUser,
  buildIdentityProviderRequest,
  handleAccessSsoLoginResponse,
  handleIdentityProviderError,
  finishLogoutRedirect,
  persistIdTokenSession,
  handleGetIdTokenError,
} from 'utils/accessSso';

type GetIdTokenSagaAction = SagaAction<GetIdTokenParams>;

export function* getIdToken({ payload }: GetIdTokenSagaAction) {
  try {
    const { id_token: idToken }: GetIdTokenV2Response = yield call(
      requestGetIdTokenV2,
      payload
    );
    persistIdTokenSession(idToken);
    yield call(getRegionIds);
    const queryParam = window?.location?.search || '';
    yield put(
      setRedirectUrl({
        url: routes.COMPANIES_SELECT.path + queryParam,
        shouldReplaceUrl: true,
      })
    );
  } catch (error) {
    handleGetIdTokenError(error);
  }
}

export function* getRegionIds() {
  yield put(getRegionIdsRequest());
  const regionIdsList: GetRegionIdsResponse = yield call(requestRegionIds);
  const regionIds = regionIdsList.map((item) => item.regionId);
  yield put(getRegionIdsSuccess({ regionIds }));
}

const warn = (message: string, error?: unknown) =>
  console.warn(message, error instanceof Error ? error.message : '');

export function* getAccessLogoutRedirectUri() {
  try {
    const { redirectUri }: GetAccessLogoutEndpointResponse = yield call(
      getAccessLogoutEndpoint
    );
    return isEmpty(redirectUri) ? null : redirectUri;
  } catch (error) {
    warn('Failed to resolve Access logout endpoint', error);
    return null;
  }
}

export function* logOut() {
  try {
    yield fork(logoutApi);
    yield delay(300);
    const mfaEnabled = isMfaEnabled();
    const accessLogoutUri = isAccessFederatedUser()
      ? yield call(getAccessLogoutRedirectUri)
      : null;
    yield clearSessionFromStorageAndCookies();
    finishLogoutRedirect({ accessLogoutUri, mfaEnabled });
  } catch (error) {
    redirectToVcIdentity();
  }
}

type AccessSsoLoginSagaAction = SagaAction<AccessSsoLoginParams>;

export function* accessSsoLogin({ payload }: AccessSsoLoginSagaAction) {
  const { email, evoRegion, callbackURL } =
    payload || ({} as AccessSsoLoginParams);
  const search = buildCallbackSearch(callbackURL);
  if (!isValidEmail(email)) {
    redirectToVcIdentity(search);
    return;
  }

  try {
    const response: GetIdentityProviderResponse = yield call(
      requestIdentityProvider,
      buildIdentityProviderRequest({ email, search })
    );
    handleAccessSsoLoginResponse(response, { email, evoRegion, search });
  } catch (error) {
    handleIdentityProviderError(error, { email, search });
  }
}

export default [
  takeLatest(sagaActions.GET_ID_TOKEN, getIdToken),
  takeLatest(sagaActions.ACCESS_SSO_LOGIN, accessSsoLogin),
  takeLatest(sagaActions.LOGOUT, logOut),
];
